import { useTranslation } from "react-i18next";
import { IconCheck } from "@tabler/icons-react";
import { Select } from "../ui/Select";
import { openDesktopSettings } from "../../domains/app/stores/desktopSettingsStore";
import type { PracticeRailCorpus } from "../../domains/practice/hooks/usePracticeRailTerms";

/**
 * The practice, as the desktop top bar's dropdown (Figma "Select",
 * Appearance=Inline): the corpus being read, in place of a page title, with
 * "All practices" for a reader holding more than one.
 *
 * Bare text and a chevron rather than a pill: the practice is the CORPUS being
 * read, not a filter added to it, so it must not read as a peer of the chips
 * below. The trigger truncates a long name; the panel, wider than the trigger,
 * gives every name its whole length.
 *
 * Desktop only. The mobile header keeps its own `PracticeDropdown`.
 */
function DesktopPracticeDropdown({
  practices,
  corpus,
  onSelectCorpus,
}: {
  practices: ReadonlyArray<{ uid: string; name: string }>;
  /** `null` while the catalogue has not landed: the button renders no name. */
  corpus: PracticeRailCorpus | null;
  onSelectCorpus: (corpus: PracticeRailCorpus) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  // "All practices" is offered ONLY to a reader holding more than one, because
  // with a single practice it is that practice under a second name: two rows,
  // one corpus, and a reader left wondering what the difference is. It leads
  // the list because it is not a peer of the rows below, it is all of them.
  // It is also kept while it IS the corpus (a `?practice=all` link before the
  // catalogue lands), so the trigger never loses its label.
  const options = [
    ...(practices.length > 1 || (corpus !== null && corpus.kind === "all")
      ? [
          {
            value: ALL_PRACTICES_VALUE,
            label: t("All practices"),
            corpus: { kind: "all" } satisfies PracticeRailCorpus,
          },
        ]
      : []),
    ...practices.map((practice) => ({
      value: practice.uid,
      label: practice.name,
      corpus: {
        kind: "practice",
        uid: practice.uid,
      } satisfies PracticeRailCorpus,
    })),
  ];

  return (
    <Select
      appearance="inline"
      value={
        corpus === null
          ? ""
          : corpus.kind === "all"
            ? ALL_PRACTICES_VALUE
            : corpus.uid
      }
      onChange={(option) => {
        onSelectCorpus(option.corpus);
      }}
      options={options}
      // A practice name runs to 60 characters: in the panel it wraps rather
      // than losing its end.
      renderOption={(option, isSelected) => (
        <>
          <span className="min-w-0 flex-1 text-ui-body text-ds-black">
            {option.label}
          </span>
          {isSelected && (
            <IconCheck size={16} className="shrink-0 text-ds-primary" />
          )}
        </>
      )}
      footerAction={{
        label: t("Manage practices"),
        panel: "close",
        onSelect: () => {
          openDesktopSettings({ page: "practice" });
        },
      }}
      searchOnlyOptions={null}
    />
  );
}

/** The "All practices" option's value, which no practice uid can take. */
const ALL_PRACTICES_VALUE = "all-practices";

export { DesktopPracticeDropdown };
